import { createTheme, alpha } from '@mui/material/styles';
import { designTokens } from './design-tokens';
import { colors } from '../../shared/constants/colors';

const t = designTokens;

/**
 * MUI Dashboard Theme Configuration
 * Based on the Nexus Admin design system
 *
 * Design system spec: src/apps/admin/frontend/DESIGN.md
 */
export const dashboardTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: t.colors.primary,
      light: '#B7C4FF', // inverse-primary
      dark: '#002E8A',
      contrastText: t.colors['on-primary'],
    },
    secondary: {
      main: t.colors.secondary,
      light: t.colors['secondary-container'],
      dark: '#5A3800',
      contrastText: t.colors['on-secondary'],
    },
    error: {
      main: t.colors.error,
      light: t.colors['error-container'],
      dark: '#690005',
      contrastText: t.colors['on-error'],
    },
    warning: {
      main: t.colors.warning,
      light: alpha(t.colors.warning, 0.7),
      dark: '#B45309',
      contrastText: '#ffffff',
    },
    success: {
      main: t.colors.success,
      light: '#A7F3D0',
      dark: '#047857',
      contrastText: '#ffffff',
    },
    background: {
      default: t.colors.background,
      paper: t.colors['surface-container-lowest'],
    },
    text: {
      primary: t.colors['on-surface'],
      secondary: t.colors['on-surface-variant'],
      disabled: colors.disabled,
    },
    divider: t.colors['outline-variant'],
    grey: colors.grey,
  },

  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      'sans-serif',
    ].join(','),
    fontSize: 14,

    // Headlines
    h1: {
      fontFamily: 'Inter',
      fontSize: '32px',
      fontWeight: '700',
      lineHeight: '40px',
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: 'Inter',
      fontSize: '24px',
      fontWeight: '600',
      lineHeight: '32px',
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: 'Inter',
      fontSize: '20px',
      fontWeight: '600',
      lineHeight: '28px',
    },
    h4: {
      fontFamily: 'Inter',
      fontSize: '16px',
      fontWeight: '600',
      lineHeight: '24px',
    },
    h5: {
      fontFamily: 'Inter',
      fontSize: '14px',
      fontWeight: '600',
      lineHeight: '20px',
    },
    h6: {
      fontFamily: 'Inter',
      fontSize: '13px',
      fontWeight: '600',
      lineHeight: '18px',
      letterSpacing: '0.05em',
    },

    // Body
    body1: {
      fontFamily: 'Inter',
      fontSize: '16px',
      fontWeight: '400',
      lineHeight: '24px',
    },
    body2: {
      fontFamily: 'Inter',
      fontSize: '14px',
      fontWeight: '400',
      lineHeight: '20px',
    },

    // Labels - use caption instead of label (MUI standard)
    caption: {
      fontFamily: 'Inter',
      fontSize: '12px',
      fontWeight: '500',
      lineHeight: '16px',
    },

    // Button
    button: {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '14px',
    },
  },

  shape: {
    borderRadius: 8,
  },

  spacing: 8,

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        code: {
          fontFamily: "'Courier Prime', monospace",
        },
        pre: {
          fontFamily: "'Courier Prime', monospace",
        },
        body: {
          backgroundColor: t.colors.background,
        },
      },
    },

    // Buttons
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 8,
          height: 36,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          backgroundColor: t.colors.primary,
          color: t.colors['on-primary'],
          '&:hover': {
            backgroundColor: '#002E8A',
          },
        },
        outlined: {
          borderColor: t.colors.slate[200],
          color: t.colors.slate[800],
          '&:hover': {
            backgroundColor: alpha(t.colors.slate[100], 0.5),
            borderColor: t.colors.slate[300],
          },
        },
      },
    },

    // Text Fields
    MuiTextField: {
      defaultProps: {
        size: 'small',
        slotProps: {
          inputLabel: {
            shrink: true,
          },
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '14px',
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: t.colors.slate[300],
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: t.colors.slate[400],
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: t.colors.primary,
            boxShadow: `0 0 0 3px ${alpha(t.colors.primary, 0.15)}`,
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: t.colors.destructive,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          color: t.colors.slate[700],
          '&.Mui-focused': {
            color: t.colors.primary,
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: {
          display: 'flex',
          alignItems: 'center',
        },
      },
    },

    // Cards
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: t.colors['surface-container-lowest'],
          border: `1px solid ${t.colors.slate[200]}`,
          borderRadius: 12,
          boxShadow: 'none',
          overflow: 'hidden',
        },
      },
    },

    // Drawer / Sidebar
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: t.colors['surface-container-lowest'],
          borderRight: `1px solid ${t.colors['outline-variant']}`,
        },
      },
    },

    // App Bar
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: t.colors['surface-container-lowest'],
          color: t.colors['on-surface'],
          boxShadow: 'none',
          borderBottom: `1px solid ${t.colors['outline-variant']}`,
        },
      },
    },

    // List Item Buttons (Navigation)
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          margin: '2px 8px',
          padding: '8px 12px',
          '&.Mui-selected': {
            backgroundColor: t.colors['surface-container-low'],
            color: t.colors.primary,
            fontWeight: 600,
            borderLeft: `3px solid ${t.colors.primary}`,
            paddingLeft: 9,
            '&:hover': {
              backgroundColor: t.colors['surface-container'],
            },
          },
          '&:hover': {
            backgroundColor: alpha(t.colors.slate[400], 0.08),
          },
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 36,
          color: t.colors['on-surface-variant'],
          '.Mui-selected &': {
            color: t.colors.primary,
          },
        },
      },
    },

    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '14px',
          fontWeight: 500,
        },
      },
    },

    // Dividers
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: t.colors['outline-variant'],
        },
      },
    },

    // Avatars
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: t.colors.primary,
          color: t.colors['on-primary'],
          fontWeight: 600,
        },
      },
    },

    // Chips / Badges
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
        sizeSmall: {
          height: 20,
          fontSize: '11px',
          fontWeight: 500,
        },
      },
    },

    // Tables
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: t.colors.slate[50],
          '& .MuiTableCell-head': {
            backgroundColor: t.colors.slate[50],
            fontSize: '12px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: t.colors.slate[700],
            borderBottom: `1px solid ${t.colors.slate[200]}`,
            padding: '12px',
          },
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: t.colors.slate[50],
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${t.colors.slate[100]}`,
          padding: '12px',
        },
      },
    },

    // Dialogs / Modals
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: t.colors['surface-container-lowest'],
          border: `1px solid ${t.colors.slate[200]}`,
          borderRadius: 12,
          boxShadow: '0px 4px 6px -1px rgba(0,0,0,0.1), 0px 10px 20px -5px rgba(0,0,0,0.06)',
        },
      },
    },

    // Tooltips
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: t.colors.slate[800],
          fontSize: '12px',
          borderRadius: 4,
        },
      },
    },

    // Snackbar / Alert
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        standardSuccess: {
          backgroundColor: colors.statusActive.background,
          color: colors.statusActive.color,
        },
        standardWarning: {
          backgroundColor: colors.statusSuspended.background,
          color: colors.statusSuspended.color,
        },
        standardError: {
          backgroundColor: colors.statusRevoked.background,
          color: colors.statusRevoked.color,
        },
      },
    },
  },
});

export const theme = dashboardTheme;
